import { loadScript, removeScript } from "../utils/operate-script";

const PAYPAL_SDK_SCRIPT_ID = "script-paypal-sdk-id";

let paypalSdkPromise: Promise<any> | null = null;
let loadedSdkSrc = "";

export interface PaypalSdkOptions {
  clientId: string;
  currency: string;
  buyerCountry?: string;
  components?: string[];
  enableFunding?: string[];
  disableFunding?: string[];
  intent?: "capture" | "authorize";
}

const buildPaypalSdkSrc = ({
  clientId,
  currency,
  buyerCountry,
  components = ["buttons", "funding-eligibility"],
  enableFunding = ["card"],
  disableFunding = [],
  intent = "capture",
}: PaypalSdkOptions) => {
  const searchParams = new URLSearchParams({
    "client-id": clientId,
    currency,
    components: components.join(","),
    intent,
  });

  if (enableFunding.length) {
    searchParams.set("enable-funding", enableFunding.join(","));
  }

  if (disableFunding.length) {
    searchParams.set("disable-funding", disableFunding.join(","));
  }

  if (buyerCountry) {
    searchParams.set("buyer-country", buyerCountry);
  }

  return `https://www.paypal.com/sdk/js?${searchParams.toString()}`;
};

export async function loadPaypalSdk(options: PaypalSdkOptions): Promise<any> {
  if (!options.clientId) {
    throw new Error("PayPal client ID is missing.");
  }

  const sdkSrc = buildPaypalSdkSrc(options);
  if (window.paypal && loadedSdkSrc === sdkSrc) {
    return window.paypal;
  }

  if (paypalSdkPromise && loadedSdkSrc === sdkSrc) {
    return paypalSdkPromise;
  }

  if (loadedSdkSrc && loadedSdkSrc !== sdkSrc) {
    removeScript(PAYPAL_SDK_SCRIPT_ID);
    window.paypal = undefined;
  }

  loadedSdkSrc = sdkSrc;
  paypalSdkPromise = loadScript(sdkSrc, PAYPAL_SDK_SCRIPT_ID)
    .then(() => {
      if (!window.paypal) {
        throw new Error("PayPal SDK failed to initialize.");
      }

      return window.paypal;
    })
    .catch((error) => {
      paypalSdkPromise = null;
      throw error;
    });

  return paypalSdkPromise;
}
